import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { Edit, Shield, FileText } from 'lucide-react'
import apiService from '../services/api'
import toast from 'react-hot-toast'
import PolitiqueConfidentialite from './PolitiqueConfidentialite'
import CGV from './CGV'
import MentionsLegales from './MentionsLegales'
import Contact from './Contact'
import { sanitizeHtml } from '../utils/sanitizeHtml'

const PageCMS = ({ slug: propSlug }) => {
  const { slug: paramSlug } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  
  // Utiliser le slug depuis les props, les params, ou l'URL
  const slug = propSlug || paramSlug || location.pathname.replace('/', '')
  
  // Composants statiques par slug
  const staticComponents = {
    'politique-confidentialite': PolitiqueConfidentialite,
    'cgv': CGV,
    'mentions-legales': MentionsLegales,
    'contact': Contact
  }
  
  const StaticComponent = staticComponents[slug]
  const [isAdmin, setIsAdmin] = useState(false)

  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem('admin_token') || localStorage.getItem('user_token')
        if (token) {
          // Vérifier si le token est valide et si l'utilisateur est admin
          const response = await apiService.get('/auth/me')
          const user = response.data?.user || response.data?.admin
          if (user?.role === 'admin') {
            setIsAdmin(true)
          }
        }
      } catch (error) {
        setIsAdmin(false)
      }
    }
    checkAdmin()
  }, [])

  // Charger la page depuis l'API (seulement si le slug est défini)
  const { data, isLoading, error } = useQuery(
    ['cms-page', slug],
    () => apiService.get(`/pages/public/${slug}`).then(res => res.data),
    {
      enabled: !!slug,
      retry: false, // Ne pas réessayer si 404 (page n'existe pas en DB)
      onError: (err) => {
        if (err?.response?.status === 404) {
          // Page non trouvée en DB, on utilisera le composant statique
          console.log('Page non trouvée en base de données, utilisation du composant statique')
        }
      }
    }
  )

  const page = data?.page

  const handleEdit = () => {
    // Rediriger vers l'admin avec l'onglet pages et la page pré-sélectionnée
    navigate(`/admin/settings?tab=pages&edit=${slug}`)
  }

  // Si un composant statique existe, l'utiliser en priorité si la page n'existe pas en DB
  // Sinon, afficher le contenu de la DB si disponible
  if (isLoading && !StaticComponent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de la page...</p>
        </div>
      </div>
    )
  }

  // Si la page n'existe pas en DB mais qu'un composant statique existe, l'utiliser
  if ((error || !page) && StaticComponent) {
    return (
      <div className="relative">
        {/* Bouton Modifier (visible uniquement pour les admins) */}
        {isAdmin && (
          <div className="fixed bottom-4 right-4 z-50">
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg shadow-lg hover:bg-orange-600 transition-colors"
              title="Migrer cette page vers le CMS"
            >
              <Edit className="h-4 w-4 mr-2" />
              Migrer vers CMS
            </button>
          </div>
        )}
        <StaticComponent />
      </div>
    )
  }
  
  // Si on charge et qu'un composant statique existe, l'afficher pendant le chargement
  if (isLoading && StaticComponent) {
    return (
      <div className="relative">
        {isAdmin && (
          <div className="fixed bottom-4 right-4 z-50">
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg shadow-lg hover:bg-orange-600 transition-colors"
              title="Migrer cette page vers le CMS"
            >
              <Edit className="h-4 w-4 mr-2" />
              Migrer vers CMS
            </button>
          </div>
        )}
        <StaticComponent />
      </div>
    )
  }
  
  // Si la page n'existe ni en DB ni en composant statique
  if (error || !page) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container-custom py-12">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Page non trouvée</h1>
            <p className="text-gray-600 mb-6">
              Cette page n'a pas encore été créée dans le système de gestion de contenu.
            </p>
            {isAdmin && (
              <button
                onClick={handleEdit}
                className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Créer cette page
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Bouton Modifier (visible uniquement pour les admins) */}
      {isAdmin && (
        <div className="fixed bottom-4 right-4 z-50">
          <button
            onClick={handleEdit}
            className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg shadow-lg hover:bg-orange-600 transition-colors animate-pulse"
            title="Modifier cette page"
          >
            <Edit className="h-4 w-4 mr-2" />
            Modifier
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container-custom py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center mb-4">
              <Shield className="h-8 w-8 text-orange-500 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">{page.title}</h1>
            </div>
          </div>
          {page.meta_description && (
            <p className="text-gray-600">{page.meta_description}</p>
          )}
          {page.updated_at && (
            <p className="text-sm text-gray-500 mt-2">
              Dernière mise à jour : {new Date(page.updated_at).toLocaleDateString('fr-FR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container-custom py-12">
        <div 
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content) }}
        />
      </div>
    </div>
  )
}

export default PageCMS
